/**
 * Mirror of server/src/lib/billingCatalog.ts — client-side copy so the UI can
 * enumerate tiers / terms / cadences without a server round-trip on every
 * render. The server exposes the same data at GET /billing/catalog; keep
 * this mirror in sync with server/src/lib/billingCatalog.ts.
 */

export const BILLING_PLANS = ['starter', 'pro', 'enterprise'] as const;
export type BillingPlan = (typeof BILLING_PLANS)[number];

export const BILLING_CADENCES = ['monthly', 'quarterly'] as const;
export type BillingCadence = (typeof BILLING_CADENCES)[number];

export const COMMITMENT_TERM_MONTHS = [0, 12] as const;
export type CommitmentTermMonths = (typeof COMMITMENT_TERM_MONTHS)[number];

export interface BillingCatalogEntry {
  priceKey: string;
  plan: BillingPlan;
  termMonths: CommitmentTermMonths;
  cadence: BillingCadence;
  iterations: number | null;
  intervalCount: 1 | 3;
}

export function buildPriceKey(
  plan: BillingPlan,
  termMonths: CommitmentTermMonths,
  cadence: BillingCadence,
): string | null {
  if (termMonths === 0 && cadence === 'quarterly') return null;
  const termSuffix = termMonths === 0 ? 'm2m' : `${termMonths / 12}yr`;
  return `${plan}_${cadence}_${termSuffix}`;
}

export function planDisplayName(plan: string): string {
  if (!plan) return '—';
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

export function termDisplayName(termMonths: CommitmentTermMonths): string {
  switch (termMonths) {
    case 0:
      return 'Month-to-month';
    case 12:
      return '1-year commitment';
    default:
      return `${termMonths} months`;
  }
}

export function cadenceDisplayName(cadence: BillingCadence): string {
  return cadence === 'monthly' ? 'Monthly' : 'Quarterly';
}

/** Discount applied by commitment length (informational only; priced in Stripe). */
export function termDiscountPct(termMonths: CommitmentTermMonths): number {
  switch (termMonths) {
    case 12:
      return 10;
    default:
      return 0;
  }
}
